import { Link } from "react-router-dom";
import { ImageWithFallback } from "./ImageWithFallback";
import iphone1 from "../../assets/landing/uae-1.jpg";
import iphone2 from "../../assets/landing/uae-2.jpg";
import iphone3 from "../../assets/landing/uae-3.jpg";

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="lp-w-5 lp-h-5" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const AndroidIcon = () => (
  <svg viewBox="0 0 24 24" className="lp-w-5 lp-h-5" fill="currentColor">
    <path d="M3 20.5v-17c0-.83.94-1.3 1.6-.8l14 8.5c.6.36.6 1.24 0 1.6l-14 8.5c-.66.5-1.6.03-1.6-.8z" />
  </svg>
);

export function LandingHero() {
  return (
    <section className="lp-relative lp-flex lp-flex-col lp-items-center lp-justify-center lp-overflow-hidden lp-pt-32 lp-pb-16 lp-px-6" style={{ minHeight: "100vh" }}>
      {/* Background glow overlay */}
      <div
        aria-hidden
        className="lp-absolute lp-left-half lp-tx-neg-half lp-pointer-none lp-op-15"
        style={{
          top: 0,
          width: 900,
          height: 600,
          borderRadius: "50%",
          background: "radial-gradient(ellipse, rgba(74, 158, 255, 0.15) 0%, transparent 70%)",
          zIndex: 1,
        }}
      />

      {/* Modern dotted/grid overlay */}
      <div
        aria-hidden
        className="lp-absolute lp-inset-0 lp-op-06 lp-pointer-none"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(74, 158, 255, 0.1) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          zIndex: 1,
        }}
      />

      {/* Decorative background gradient overlay for depth */}
      <div
        aria-hidden
        className="lp-absolute lp-inset-0 lp-pointer-none lp-op-30"
        style={{
          background: "radial-gradient(ellipse 80% 60% at 70% 40%, rgba(74, 158, 255, 0.08) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 30% 70%, rgba(36, 197, 247, 0.05) 0%, transparent 70%)",
          zIndex: 1,
        }}
      />

      <div className="lp-max-6xl lp-mx-auto lp-grid lp-grid-cols-1 md-cols-2 lp-gap-12 lp-items-center lp-relative lp-z-10">
        
        {/* Left Column: Headline and text */}
        <div>
          {/* UAE Live Badge */}
          <div className="lp-iflex lp-items-center lp-gap-2 lp-px-3 lp-py-15 lp-r-full lp-border lp-bc-b10 lp-bg-b05 lp-mb-6">
            <span className="lp-w-15 lp-h-15 lp-r-full lp-bg-blue lp-pulse" />
            <span className="lp-text-xs lp-text-muted" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Now live across the UAE
            </span>
          </div>

          <h1
            className="lp-text-dark lp-mb-6 lp-leading-tight"
            style={{
              fontFamily: "'Fraunces', serif",
              fontWeight: 600,
              letterSpacing: "-0.03em",
              fontSize: "clamp(2.4rem, 5vw, 4.2rem)",
            }}
          >
            Discover the Best{" "}
            <span className="lp-text-blue" style={{ fontStyle: "italic" }}>Cafés</span>,{" "}
            <span className="lp-text-blue" style={{ fontStyle: "italic" }}>Restaurants</span> & Nightlife in the UAE
          </h1>

          <div
            className="lp-text-muted lp-mb-10 lp-leading-relaxed lp-flex lp-flex-col lp-gap-4"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300, fontSize: "1.08rem" }}
          >
            <p>
              Hubscroll is the easiest way to discover the most exciting cafés, restaurants, lounges, and nightlife venues across Dubai and the UAE.
            </p>
            <p>
              Browse venues, explore photos, read reviews, and discover trending spots — all inside one beautifully designed mobile app.
            </p>
            <p className="lp-font-medium lp-text-dark">
              Download Hubscroll today and start exploring the UAE like never before.
            </p>
          </div>

          {/* Download App Row */}
          <div>
            <div className="lp-text-xs lp-text-muted lp-uppercase lp-tracking-wide lp-mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Download the Hubscroll App
            </div>
            <div className="lp-flex lp-flex-wrap lp-gap-4">
              <a
                href="#download"
                className="lp-flex lp-items-center lp-gap-25 lp-px-5 lp-py-2 lp-r-full lp-bg-white lp-text-dark lp-transition lp-hov-scale lp-border"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
              >
                <AppleIcon />
                App Store
              </a>
              <a
                href="#download"
                className="lp-flex lp-items-center lp-gap-25 lp-px-5 lp-py-2 lp-r-full lp-border lp-bc-b15 lp-text-dark lp-transition lp-hov-scale"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
              >
                <AndroidIcon />
                Google Play
              </a>
            </div>
            
            {/* Secondary Business CTA */}
            <Link
              to="/sign-in"
              className="lp-iflex lp-items-center lp-gap-1 lp-text-sm lp-text-blue lp-mt-6 lp-transition lp-hov-scale"
              style={{ fontFamily: "'Outfit', sans-serif", borderBottom: "1px solid transparent" }}
              onMouseEnter={(e) => e.target.style.borderBottomColor = "#4A9EFF"}
              onMouseLeave={(e) => e.target.style.borderBottomColor = "transparent"}
            >
              Are you a business? List your venue &rarr;
            </Link>
          </div>
        </div>

        {/* Right Column: Layered 3 iPhone mockup with visual depth */}
        <div className="lp-relative lp-flex lp-justify-center lp-items-center lp-w-full lp-py-10">
          
          {/* Giant backdrop background word "UAE" layer for depth */}
          <div
            aria-hidden
            className="lp-absolute lp-pointer-none lp-op-06 lp-nowrap"
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: "14rem",
              fontWeight: 800,
              color: "rgba(255, 255, 255, 0.04)",
              letterSpacing: "-0.05em",
              zIndex: 0,
              transform: "translateY(-10px)",
              userSelect: "none",
            }}
          >
            UAE
          </div>

          {/* Overlapping, tilted phones container */}
          <div className="lp-relative lp-z-10 lp-flex lp-items-end lp-justify-center lp-w-full lp-max-xl">
            {/* Left Phone: rotated left, tilted */}
            <div className="lp-w-28p lp-op-60 lp-tilt-left lp-transition" style={{ zIndex: 5 }}>
              <ImageWithFallback
                src={iphone3}
                alt="Venue discovery feed mockup"
                className="lp-w-full lp-r-24 lp-shadow-2xl"
                style={{ border: "2px solid rgba(255,255,255,0.05)" }}
              />
            </div>
            
            {/* Center Phone: Straight, glowing */}
            <div className="lp-w-36p lp-z-10" style={{ transform: "scale(1.08) translateY(-10px)", filter: "drop-shadow(0 20px 40px rgba(74, 158, 255, 0.2))" }}>
              <ImageWithFallback
                src={iphone1}
                alt="Venue profile page mockup"
                className="lp-w-full lp-r-28 lp-shadow-2xl"
                style={{ border: "3px solid rgba(255,255,255,0.08)" }}
              />
            </div>
            
            {/* Right Phone: rotated right, tilted */}
            <div className="lp-w-28p lp-op-60 lp-tilt-right lp-transition" style={{ zIndex: 5 }}>
              <ImageWithFallback
                src={iphone2}
                alt="Categories or map view mockup"
                className="lp-w-full lp-r-24 lp-shadow-2xl"
                style={{ border: "2px solid rgba(255,255,255,0.05)" }}
              />
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
